/**
 * Week-shaping helpers for callers of {@link AvailabilityGrid}.
 *
 * The grid itself never interprets a column, so which day a week starts on is
 * entirely the caller's choice. These helpers exist so every caller expresses
 * that choice the same way, and so availability can be held against a weekday
 * rather than against a column position — a distinction that matters the moment
 * the starting day is allowed to change, because the same Tuesday is a different
 * column index in a Sunday-first week than in a Monday-first one.
 */
import type {
  Dayjs,
  IsoDate,
  IsoWeekday,
  TimeRangeOnDate,
  TimeRangeOnWeekday,
} from "@thalamos/common";
import {
  addDaysToIsoWeekday,
  dayjs,
  ISO_DATE_FORMAT,
  IsoDateSchema,
  isoWeekdayOf,
  TimeOfDaySchema,
} from "@thalamos/common";

import type { AvailabilityGridColumn } from "./AvailabilityGrid.js";
import type {
  AvailabilityBlock,
  AvailabilityGridPoint,
} from "./availabilityGridModel.js";
import { START_OF_DAY } from "./availabilityGridModel.js";

const DAYS_PER_WEEK = 7;

/** The seven weekdays in the order a week beginning on `weekStartsOn` runs. */
function weekdayOrder(weekStartsOn: IsoWeekday): IsoWeekday[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_unused, offset) =>
    addDaysToIsoWeekday(weekStartsOn, offset),
  );
}

/** Where `weekday` falls in a week beginning on `weekStartsOn`. */
export function weekdayColumnIndex(
  weekday: IsoWeekday,
  weekStartsOn: IsoWeekday,
): number {
  return (weekday - weekStartsOn + DAYS_PER_WEEK) % DAYS_PER_WEEK;
}

/** Which weekday sits at `columnIndex` of a week beginning on `weekStartsOn`. */
export function weekdayAtColumnIndex(
  columnIndex: number,
  weekStartsOn: IsoWeekday,
): IsoWeekday {
  return addDaysToIsoWeekday(weekStartsOn, columnIndex);
}

/** Column heading for a day, abbreviated so seven fit a phone-width row. */
function weekdayHeading(day: Dayjs): string {
  return day.format("ddd");
}

/** Columns labelled by weekday only, for a repeating week. */
export function buildWeekdayColumns(
  weekStartsOn: IsoWeekday,
): AvailabilityGridColumn[] {
  return weekdayOrder(weekStartsOn).map((weekday) => {
    const day = dayjs().isoWeekday(weekday);
    // The key is identity, not display: the full weekday name however the heading is abbreviated.
    return {
      key: day.format("dddd").toLowerCase(),
      label: weekdayHeading(day),
    };
  });
}

/**
 * The start of the week containing `date`. Computed explicitly rather than with
 * `startOf("week")`, whose first day follows the active dayjs locale and so
 * would ignore `weekStartsOn`.
 */
export function startOfWeekContaining(
  date: Dayjs,
  weekStartsOn: IsoWeekday,
): Dayjs {
  const daysSinceWeekStart =
    (isoWeekdayOf(date) - weekStartsOn + DAYS_PER_WEEK) % DAYS_PER_WEEK;
  return date.subtract(daysSinceWeekStart, "day").startOf("day");
}

/** Columns labelled by weekday and date, for a specific week. */
export function buildDatedColumns(
  weekStart: Dayjs,
  options: { highlightDate?: Dayjs } = {},
): AvailabilityGridColumn[] {
  return Array.from({ length: DAYS_PER_WEEK }, (_unused, dayOffset) => {
    const date = weekStart.add(dayOffset, "day");
    return {
      key: date.format("YYYY-MM-DD"),
      label: weekdayHeading(date),
      subLabel: date.format("D MMM"),
      highlighted:
        options.highlightDate !== undefined &&
        date.isSame(options.highlightDate, "day"),
    };
  });
}

/** e.g. `"7 - 13 Sep 2026"`, spelling out only what the two ends disagree on. */
export function formatWeekLabel(weekStart: Dayjs): string {
  const weekEnd = weekStart.add(DAYS_PER_WEEK - 1, "day");
  if (!weekStart.isSame(weekEnd, "year")) {
    return `${weekStart.format("D MMM YYYY")} - ${weekEnd.format("D MMM YYYY")}`;
  }
  if (!weekStart.isSame(weekEnd, "month")) {
    return `${weekStart.format("D MMM")} - ${weekEnd.format("D MMM YYYY")}`;
  }
  return `${weekStart.format("D")} - ${weekEnd.format("D MMM YYYY")}`;
}

/**
 * Dates are walked in UTC, where no day is ever 23 or 25 hours long, so a clock
 * change can neither drop a date nor produce one twice.
 */
function utcDate(date: string): Dayjs {
  return dayjs.utc(date, ISO_DATE_FORMAT);
}

/** Whole calendar days from `weekStart` to `date`, negative before the week. */
function dayOffsetOf(weekStart: Dayjs, date: string): number {
  return utcDate(date).diff(utcDate(weekStart.format(ISO_DATE_FORMAT)), "day");
}

/** The date `dayOffset` days into the week beginning `weekStart`. */
function dateAtColumnIndex(weekStart: Dayjs, dayOffset: number): IsoDate {
  return IsoDateSchema.parse(
    utcDate(weekStart.format(ISO_DATE_FORMAT))
      .add(dayOffset, "day")
      .format(ISO_DATE_FORMAT),
  );
}

/**
 * Where `instant` falls in the week beginning `weekStart`, for a caller shading
 * time that has passed.
 *
 * Clamped to the week's own edges: before it, the point sits at the very start
 * so nothing shades; at or after it, the point sits past the last column so
 * everything does. The time is `instant`'s wall clock in its own zone, to the
 * minute — a grid never resolves finer than that.
 */
export function gridPointForInstant(
  weekStart: Dayjs,
  instant: Dayjs,
): AvailabilityGridPoint {
  const columnIndex = dayOffsetOf(weekStart, instant.format(ISO_DATE_FORMAT));

  if (columnIndex < 0) {
    return { columnIndex: 0, time: START_OF_DAY };
  }
  if (columnIndex >= DAYS_PER_WEEK) {
    return { columnIndex: DAYS_PER_WEEK, time: START_OF_DAY };
  }
  return {
    columnIndex,
    time: TimeOfDaySchema.parse(instant.format("HH:mm:00")),
  };
}

/** Places a repeating week's ranges in a grid beginning on `weekStartsOn`. */
export function weekdayRangesToBlocks(
  ranges: readonly TimeRangeOnWeekday[],
  weekStartsOn: IsoWeekday,
): AvailabilityBlock[] {
  return ranges.map((range) => ({
    columnIndex: weekdayColumnIndex(range.dayOfWeek, weekStartsOn),
    from: range.from,
    until: range.until,
  }));
}

/** Reads a grid beginning on `weekStartsOn` back as a repeating week. */
export function blocksToWeekdayRanges(
  blocks: readonly AvailabilityBlock[],
  weekStartsOn: IsoWeekday,
): TimeRangeOnWeekday[] {
  return blocks.map((block) => ({
    dayOfWeek: weekdayAtColumnIndex(block.columnIndex, weekStartsOn),
    from: block.from,
    until: block.until,
  }));
}

/**
 * Places dated ranges in the week beginning `weekStart`. A range on a date
 * outside that week has no column to go in and is left out, so a caller may
 * pass everything it holds and let the week decide what it shows.
 */
export function dateRangesToBlocks(
  ranges: readonly TimeRangeOnDate[],
  weekStart: Dayjs,
): AvailabilityBlock[] {
  const blocks: AvailabilityBlock[] = [];
  for (const range of ranges) {
    const columnIndex = dayOffsetOf(weekStart, range.date);
    if (columnIndex < 0 || columnIndex >= DAYS_PER_WEEK) {
      continue;
    }
    blocks.push({ columnIndex, from: range.from, until: range.until });
  }
  return blocks;
}

/** Reads the week beginning `weekStart` back as dated ranges. */
export function blocksToDateRanges(
  blocks: readonly AvailabilityBlock[],
  weekStart: Dayjs,
): TimeRangeOnDate[] {
  return blocks.map((block) => ({
    date: dateAtColumnIndex(weekStart, block.columnIndex),
    from: block.from,
    until: block.until,
  }));
}
